// RecipeNarrationButton — admin-only narration controls.
//
// Renders nothing for non-admins. For admins, two tiers of control:
//
//   1. Top button: "🎙 Generate narration" (no audio yet) or
//      "Re-generate narration" (full re-bake of every chunk).
//   2. Per-chunk pills: shown when audio.intro / audio.steps exist.
//      Each pill's ↻ button re-bakes ONLY that chunk via the edge
//      function's chunkKey filter — surgical fix for a single bad
//      slot, saves the full-recipe ElevenLabs cost.
//
// Cost discipline: paid AI feature, server-gated to admin via the
// is_admin() RPC on the edge function. Per memory feedback_optional_paid_ai,
// any non-admin invocation gets rejected at the server even if the
// UI gate fails.

import { useState } from "react";
import { FONT } from "../lib/tokens";
import { generateRecipeNarration } from "../lib/generateRecipeNarration";

export default function RecipeNarrationButton({ recipe, isAdmin }) {
  const [busy, setBusy] = useState(null);   // null | "all" | chunkKey
  const [errMsg, setErr] = useState("");

  if (!isAdmin) return null;

  const id = recipe?.id;
  const audio = recipe?.recipe?.audio;
  const hasIntro = !!audio?.intro?.url;
  const stepKeys = Object.keys(audio?.steps || {})
    // Stable order: stepN keys numerically, anything else alphabetic.
    // Without this, JSON-key order from the DB sometimes lands as
    // step10 before step2.
    .sort((a, b) => {
      const an = a.match(/^step(\d+)$/)?.[1];
      const bn = b.match(/^step(\d+)$/)?.[1];
      if (an && bn) return Number(an) - Number(bn);
      return a.localeCompare(b);
    });
  const hasAudio = hasIntro || stepKeys.length > 0;

  const fire = async (chunkKey) => {
    if (!id || busy) return;
    setBusy(chunkKey || "all");
    setErr("");
    const { error } = await generateRecipeNarration({
      recipeId: id,
      chunkKey,                            // undefined → full re-bake
    });
    if (error) setErr(error);
    setBusy(null);
  };

  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      {/* First-time bake button only — appears ONLY when the recipe
          has no audio yet. The intent is unambiguous (admin wants
          narration for a recipe that doesn't have it).
          Once audio exists, this button is suppressed entirely so
          a stray tap can't burn the full ElevenLabs cost on a
          recipe that's already been baked. Surgical re-bakes go
          through the per-chunk pills below — each is ~$0.05 vs
          the ~$0.30-0.60 of a full recipe re-bake. */}
      {!hasAudio && (
        <button
          onClick={() => fire(null)}
          disabled={!!busy || !id}
          style={{
            alignSelf: "flex-start",
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px",
            background: busy === "all" ? "rgba(124,94,44,0.18)" : "transparent",
            border: "1px dashed rgba(124,94,44,0.40)",
            borderRadius: 999,
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
            letterSpacing: "0.14em", textTransform: "uppercase",
            color: "#5C3F18",
            cursor: busy ? "default" : "pointer",
          }}
        >
          <span aria-hidden>🎙</span>
          {busy === "all" ? "Generating…" : "Generate narration · Admin"}
        </button>
      )}

      {/* Per-chunk re-bake pills. Only render when audio exists —
          before the first generation there's nothing to re-bake. */}
      {hasAudio && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {hasIntro && (
            <ChunkPill
              label="intro"
              busy={busy === "intro"}
              disabled={!!busy}
              onClick={() => fire("intro")}
            />
          )}
          {stepKeys.map((key) => (
            <ChunkPill
              key={key}
              label={key}
              busy={busy === key}
              disabled={!!busy}
              onClick={() => fire(key)}
            />
          ))}
        </div>
      )}

      {errMsg && (
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, color: "#A04230",
        }}>
          {errMsg}
        </div>
      )}
    </div>
  );
}

function ChunkPill({ label, busy, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={`Re-bake "${label}" only`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px",
        background: busy ? "rgba(124,94,44,0.18)" : "rgba(255,252,241,0.65)",
        border: "1px solid rgba(124,94,44,0.30)",
        borderRadius: 999,
        fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
        letterSpacing: "0.10em", textTransform: "uppercase",
        color: "#5C3F18",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled && !busy ? 0.55 : 1,
      }}
    >
      <span aria-hidden>{busy ? "⏳" : "↻"}</span>
      {label}
    </button>
  );
}
